// navigation-completion Stage C — frontend API client for the organisation
// navigation policy and recorded exceptions.
//
// This is a SEPARATE module from v2Client.ts (which this phase does not edit); it
// reuses v2Client's exported `apiFetch` so auth headers, the base URL and error
// handling are shared, never re-implemented. The regional criteria catalogue is
// fetched from the backend (`/navigation-policy/catalogue`) — the frontend never
// re-states the regional rules.

import { apiFetch } from '@/api/v2Client';

export const NAVIGATION_OPERATIONS = [
  'createBookmark',
  'createHyperlink',
  'modifyBookmark',
  'modifyHyperlink',
  'style',
  'manual',
  'linkExistingToc',
  'formatConversion',
] as const;
export type NavigationOperation = (typeof NAVIGATION_OPERATIONS)[number];

export type NavigationPreset = 'automatic' | 'preserve' | 'custom';
export type PublishingQcChoice = 'none' | 'designated' | 'dual';
export type IntakeApprovalChoice = 'none' | 'attestation' | 'verified-record';

export interface DisabledCriterionWire {
  readonly criterionId: string;
  readonly profile?: string;
}

export interface NavigationPolicyWire {
  readonly version: number;
  readonly activated: boolean;
  readonly preset: NavigationPreset;
  readonly operations: Record<NavigationOperation, boolean>;
  readonly publishingQc: PublishingQcChoice;
  readonly intakeApproval: IntakeApprovalChoice;
  readonly disabledCriteria: readonly DisabledCriterionWire[];
  readonly createdBy: string;
  readonly createdAt: string | null;
}

export interface ProfileMetaWire {
  readonly profile: string;
  readonly label: string;
  readonly authority: string;
  readonly specification: string;
  readonly version: string;
  readonly effectiveDate: string | null;
  readonly url: string;
  readonly evidenceLevel: string;
  readonly qualifiedPreset: boolean;
  readonly inheritsFrom: string | null;
  readonly advisory: string | null;
}

export interface CriterionSpecWire {
  readonly id: string;
  readonly category: string;
  readonly kind: 'regional' | 'structural-integrity';
  readonly title: string;
  readonly machineCheckable: boolean;
  readonly defaultEnabled: boolean;
}

export interface CriterionSourceWire {
  readonly authority: string;
  readonly specification: string;
  readonly version: string;
  readonly effectiveDate: string | null;
  readonly section: string;
  readonly url: string;
}

export interface CriterionMappingWire {
  readonly criterionId: string;
  readonly profile: string;
  readonly severity: 'error' | 'warning' | 'recommendation';
  readonly authorityLevel: 'formal-validation' | 'guidance';
  /** App quality check, not a sourced regional obligation. */
  readonly internalQuality?: boolean;
  readonly source: CriterionSourceWire;
  readonly pageThreshold: number | null;
  readonly thresholdBasis: string | null;
  readonly moduleScope: readonly string[] | null;
  readonly ruleIds: readonly string[];
  readonly automatedCriterionRemoved: boolean;
  readonly requiresJustification: boolean;
  readonly notes: string | null;
}

export interface NavigationCatalogue {
  readonly profiles: readonly ProfileMetaWire[];
  readonly criteria: readonly CriterionSpecWire[];
  readonly mappings: readonly CriterionMappingWire[];
}

export interface GetPolicyResponse {
  readonly ok: true;
  readonly policy: NavigationPolicyWire;
  readonly catalogue: NavigationCatalogue;
}

export interface CatalogueResponse {
  readonly ok: true;
  readonly catalogue: NavigationCatalogue;
}

export type NavigationPolicyChangeBody =
  | {
      readonly kind: 'activate';
      readonly preset: NavigationPreset;
      readonly publishingQc: PublishingQcChoice;
      readonly intakeApproval: IntakeApprovalChoice;
      readonly customOperations?: Record<NavigationOperation, boolean>;
      readonly disabledCriteria?: readonly DisabledCriterionWire[];
    }
  | {
      readonly kind: 'validate-only';
      readonly disabledCriteria?: readonly DisabledCriterionWire[];
    };

export interface UpdatePolicyBody {
  readonly expectedVersion: number;
  readonly change: NavigationPolicyChangeBody;
}

export interface UpdatePolicyResponse {
  readonly ok: true;
  readonly policy: NavigationPolicyWire;
}

export const navigationPolicy = {
  get: (): Promise<GetPolicyResponse> => apiFetch('/api/v1/navigation-policy'),
  getCatalogue: (): Promise<CatalogueResponse> => apiFetch('/api/v1/navigation-policy/catalogue'),
  update: (body: UpdatePolicyBody): Promise<UpdatePolicyResponse> =>
    apiFetch('/api/v1/navigation-policy', { method: 'PUT', json: body }),
};

// ---------------------------------------------------------------------------
// Recorded exceptions
// ---------------------------------------------------------------------------

export type NavigationExceptionStatus = 'pending' | 'approved' | 'revoked';

export interface ExceptionEvaluationWire {
  readonly valid: boolean;
  readonly reason?: string;
  readonly detail?: string;
}

export interface NavigationExceptionWire {
  readonly id: string;
  readonly submissionId: string;
  readonly documentId: string;
  readonly artifactSha256: string;
  readonly policyVersion: number;
  readonly criterionId: string;
  readonly profile: string;
  readonly targetContext: unknown | null;
  readonly reason: string;
  readonly evidence: unknown | null;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
  readonly revokedBy: string | null;
  readonly status: NavigationExceptionStatus;
  readonly version: string;
  readonly evaluation?: ExceptionEvaluationWire;
}

export interface ListExceptionsResponse {
  readonly ok: true;
  readonly exceptions: readonly NavigationExceptionWire[];
  readonly currentPolicyVersion: number;
}

export interface RequestExceptionBody {
  readonly documentId: string;
  /** Confirmation of the current server-side bytes (NOT authoritative). */
  readonly expectedArtifactHash: string;
  readonly expectedRevision?: number;
  readonly expectedPolicyVersion?: number;
  readonly expectedTargetScopeFingerprint?: string;
  readonly criterionId: string;
  readonly profile: string;
  readonly reason: string;
  readonly targetContext?: unknown;
  readonly evidence?: unknown;
  readonly expiresAt?: string | null;
}

export interface ExceptionMutationResponse {
  readonly ok: true;
  readonly exception: NavigationExceptionWire;
}

export const navigationExceptions = {
  list: (submissionId: string): Promise<ListExceptionsResponse> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-exceptions`),
  request: (submissionId: string, body: RequestExceptionBody): Promise<ExceptionMutationResponse> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-exceptions`, {
      method: 'POST',
      json: body,
    }),
  approve: (submissionId: string, id: string, expectedVersion: string): Promise<ExceptionMutationResponse> =>
    apiFetch(
      `/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-exceptions/${encodeURIComponent(id)}/approve`,
      { method: 'POST', json: { expectedVersion } },
    ),
  revoke: (submissionId: string, id: string, expectedVersion: string): Promise<ExceptionMutationResponse> =>
    apiFetch(
      `/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-exceptions/${encodeURIComponent(id)}/revoke`,
      { method: 'POST', json: { expectedVersion } },
    ),
};
